import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { isSigningOut } from "@/lib/sign-out";

type AuthState = {
  session: Session | null;
  user: User | null;
  loading: boolean;
};

const AuthContext = createContext<AuthState>({ session: null, user: null, loading: true });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // A password-reset link may land on any page (e.g. the homepage if the
    // reset page isn't an allowed redirect). Always send it to the
    // "choose a new password" page instead of straight into the dashboard.
    const goToReset = () => {
      if (window.location.pathname !== "/reset-password") {
        window.location.replace("/reset-password");
      }
    };
    if (/type=recovery/.test(window.location.hash + window.location.search)) {
      sessionStorage.setItem("pw_recovery", "1");
    }
    // Register listener FIRST
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if (isSigningOut()) return;
      if (event === "PASSWORD_RECOVERY" || (s && sessionStorage.getItem("pw_recovery"))) {
        sessionStorage.removeItem("pw_recovery");
        goToReset();
        return;
      }
      setSession(s);
      setLoading(false);
    });
    // Then check current session
    supabase.auth.getSession().then(({ data }) => {
      if (data.session && sessionStorage.getItem("pw_recovery")) {
        sessionStorage.removeItem("pw_recovery");
        goToReset();
        return;
      }
      setSession(data.session);
      setLoading(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
